import context, { IAddressValidator } from '@truckdown/systems';
import { email } from '@vuelidate/validators';
import { defineComponent, ref, computed, onMounted, Ref, watch, ComputedRef, nextTick } from 'vue';

interface IData {
    addressSentMessage: Ref<string>,
    addressCodeHasBeenSent: Ref<boolean>,
    username: Ref<HTMLInputElement | null>,
    code1: Ref<HTMLInputElement | null>,
    code2: Ref<HTMLInputElement | null>,
    code3: Ref<HTMLInputElement | null>,
    code4: Ref<HTMLInputElement | null>,
    code5: Ref<HTMLInputElement | null>,
    code6: Ref<HTMLInputElement | null>,
    addressValidationCode1: Ref<string>,
    addressValidationCode2: Ref<string>,
    addressValidationCode3: Ref<string>,
    addressValidationCode4: Ref<string>,
    addressValidationCode5: Ref<string>,
    addressValidationCode6: Ref<string>,
    isSendingCode: Ref<boolean>,
    isValidatingCode: Ref<boolean>,
    isValidated: Ref<boolean>,
    errorMessage: Ref<string>
}

const getData = function (props: any) {
    return {
        addressSentMessage: ref<string>(''),
        addressCodeHasBeenSent: ref<boolean>(false),
        username: ref<HTMLInputElement | null>(null),
        code1: ref<HTMLInputElement | null>(null),
        code2: ref<HTMLInputElement | null>(null),
        code3: ref<HTMLInputElement | null>(null),
        code4: ref<HTMLInputElement | null>(null),
        code5: ref<HTMLInputElement | null>(null),
        code6: ref<HTMLInputElement | null>(null),
        addressValidationCode1: ref<string>(''),
        addressValidationCode2: ref<string>(''),
        addressValidationCode3: ref<string>(''),
        addressValidationCode4: ref<string>(''),
        addressValidationCode5: ref<string>(''),
        addressValidationCode6: ref<string>(''),
        isSendingCode: ref<boolean>(false),
        isValidatingCode: ref<boolean>(false),
        isValidated: ref<boolean>(false),
        errorMessage: ref<string>('')
    } as IData;
}

interface IComputed {
    codeValue: ComputedRef<string>
}

const getComputed = function (data: IData, props: any) {
    return {
        codeValue: computed(() => {
            return data.addressValidationCode1.value
                + data.addressValidationCode2.value
                + data.addressValidationCode3.value
                + data.addressValidationCode4.value
                + data.addressValidationCode5.value
                + data.addressValidationCode6.value;
        }),
        isLoginWithPassword: computed(() => {
            return props.withPassword;
        })
    } as IComputed;
}

const getMethods = function (data: IData, computed: IComputed, ctx: any, props: any) {
    const phoneRegex = /^1?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    const emailMessage = 'A code has been sent to your email. Please check your inbox. Please enter the code:';
    const smsMessage = 'A code has been sent to the number specified via sms text message. Please enter the code:';


    return {
        updateAddress: function (ev: Event) {
            ctx.emit('update:modelValue', (ev?.target as any)?.value);
        },
        showLoginWithPassword: function () {
            ctx.emit('choosePassword');
        },
        getValidCodeValue: function (val: string) {
            if (val.length != 1 || isNaN(Number(val))) {
                return '';
            } else {
                return Number(val) + '';
            }
        },
        setCodeValueWithRefs: function (userCode: Ref<string>, value: string, next?: Ref<HTMLInputElement | null>) {
            userCode.value = this.getValidCodeValue(value);
            if (userCode.value.length == 1) {
                next?.value?.focus();
                next?.value?.select();
            }
        },
        deleteCodeValueWithRefs: function (userCode: Ref<string>, value: string, next?: Ref<HTMLInputElement | null>) {
            userCode.value = value;
            if (next && next.value) {
                next.value.focus();
                next.value.value = '';
            }
        },
        setCodeValue: async function (idx: number, value: string) {
            switch (idx) {
                case 1:
                    if (value.length === 6) {
                        this.setCodeValueWithRefs(data.addressValidationCode1, value.charAt(0), data.code2);
                        this.setCodeValueWithRefs(data.addressValidationCode2, value.charAt(1), data.code3);
                        this.setCodeValueWithRefs(data.addressValidationCode3, value.charAt(2), data.code4);
                        this.setCodeValueWithRefs(data.addressValidationCode4, value.charAt(3), data.code5);
                        this.setCodeValueWithRefs(data.addressValidationCode5, value.charAt(4), data.code6);
                        this.setCodeValueWithRefs(data.addressValidationCode6, value.charAt(5));
                        if (this.isCodeValid()) {
                            await this.validateCode();
                        }
                    } else {
                        this.setCodeValueWithRefs(data.addressValidationCode1, value.charAt(0), data.code2);
                    } 
                    break;
                case 2:
                    this.setCodeValueWithRefs(data.addressValidationCode2, value, data.code3);
                    break;
                case 3:
                    this.setCodeValueWithRefs(data.addressValidationCode3, value, data.code4);
                    break;
                case 4:
                    this.setCodeValueWithRefs(data.addressValidationCode4, value, data.code5);
                    break;
                case 5:
                    this.setCodeValueWithRefs(data.addressValidationCode5, value, data.code6);
                    break;
                case 6:
                    this.setCodeValueWithRefs(data.addressValidationCode6, value);
                    if (this.isCodeValid()) {
                        await this.validateCode();
                    }
                    break;

                default:
                    break;
            }
        },
        isCodeValid: function () {
            return computed.codeValue.value.length == 6;
        },
        isValidPhoneNumber: function (number: string) {
            if (!number || number.length == 0) {
                return true;
            }
            const match = number.match(phoneRegex);
            if ((match?.length ?? 0) > 0) {
                return true;
            }
            return false;
        },
        isValidEmail: function (value: string) {
            var emailValidator = email as any;
            return emailValidator.$validator(value) as boolean;
        },
        sendLoginValidationCode: async function () {
            if (!data.isSendingCode.value) {
                data.isSendingCode.value = true;

                try {
                    data.addressSentMessage.value = '';
                    data.errorMessage.value = '';
                    var type = '';

                    if (!props.modelValue || props.modelValue.length == 0) {
                        if (props.allowSkip) {
                            ctx.emit('skip');
                        } else {
                            data.errorMessage.value = 'Email or mobile phone number is required.';
                        }
                    }
                    else {
                        if (!props.disableEmail && this.isValidEmail(props.modelValue)) {
                            type = 'email';
                        }
                        else if (!props.disableSms && this.isValidPhoneNumber(props.modelValue)) {
                            type = 'sms';
                        }

                        if (type == 'email' || type == 'sms') {
                            try {
                                const validator = await context.getService<IAddressValidator>('IAddressValidator');
                                let res = await validator.sendValidationCode(props.modelValue);
                                data.addressCodeHasBeenSent.value = true;
                                data.addressSentMessage.value = type == 'email' ? emailMessage : smsMessage;
                                nextTick()
                                    .then(() => {
                                        data.code1.value?.focus();
                                    });
                            } catch (err: unknown) {
                                console.log('Exception thrown sending the validation code.', err);
                                data.errorMessage.value = 'There was an error sending the validation code.';
                            }
                        } else if (props.enableUsername && props.modelValue?.length > 0) {
                            ctx.emit('validated', 'username');
                        } else {
                            if (props.disableEmail) {
                                data.errorMessage.value = 'Invalid mobile phone number format.';
                            } else if (props.disableSms) {
                                data.errorMessage.value = 'Invalid email format.';
                            } else {
                                data.errorMessage.value = 'Invalid email or mobile phone number format.';
                            }
                        }
                    }
                } finally {
                    data.isSendingCode.value = false;
                }
            }
        },
        validateCode: async function () {
            if (!data.isValidatingCode.value) {
                data.isValidatingCode.value = true;
                try {
                    data.errorMessage.value = '';
                    if (this.isCodeValid()) {
                        const validator = await context.getService<IAddressValidator>('IAddressValidator');
                        let res = await validator.validateCode(props.modelValue as string, computed.codeValue.value);
                        data.addressSentMessage.value = '';
                        data.isValidated.value = true;
                        var type = this.isValidEmail(res.address ?? '') ? 'email' : 'sms';
                        ctx.emit('validated', type, res);

                    } else {
                        data.errorMessage.value = 'Please enter the 6-digit code sent to the address.';
                    }
                } catch (err) {
                    data.errorMessage.value = 'Invalid code.';
                } finally {
                    data.isValidatingCode.value = false;
                }
            }
        },
        handleKeyPress: function (idx: number, event: KeyboardEvent) {
            const currentInput = event.target as HTMLInputElement;
            if (event.key === "Backspace" && !currentInput.value) {

                switch (idx) {
                    case 1:
                        this.deleteCodeValueWithRefs(data.addressValidationCode1, '', data.code1);
                        break;
                    case 2:
                        this.deleteCodeValueWithRefs(data.addressValidationCode2, '', data.code1);
                        break;
                    case 3:
                        this.deleteCodeValueWithRefs(data.addressValidationCode3, '', data.code2);
                        break;
                    case 4:
                        this.deleteCodeValueWithRefs(data.addressValidationCode4, '', data.code3);
                        break;
                    case 5:
                        this.deleteCodeValueWithRefs(data.addressValidationCode5, '', data.code4);
                        break;
                    case 6:
                        this.deleteCodeValueWithRefs(data.addressValidationCode6, '', data.code5);
                        break;

                    default:
                        break;
                }

            } else if (event.key === "ArrowLeft") {

                const previousInput = currentInput.previousElementSibling as HTMLInputElement;
                if (previousInput) {
                    previousInput.focus();
                    previousInput.setSelectionRange(0, previousInput.value.length);
                }

            } else if (event.key === "ArrowRight") {

                const nextInput = currentInput.nextElementSibling as HTMLInputElement;
                if (nextInput) {
                    nextInput.focus();
                    nextInput.setSelectionRange(nextInput.value.length, nextInput.value.length);
                }

            } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault();
            }
        },
        limitToOneDigit: function (value: string) {
            if (value.length === 6) {
                this.setCodeValueWithRefs(data.addressValidationCode1, value.charAt(0), data.code2);
                this.setCodeValueWithRefs(data.addressValidationCode2, value.charAt(1), data.code3);
                this.setCodeValueWithRefs(data.addressValidationCode3, value.charAt(2), data.code4);
                this.setCodeValueWithRefs(data.addressValidationCode4, value.charAt(3), data.code5);
                this.setCodeValueWithRefs(data.addressValidationCode5, value.charAt(4), data.code6);
                this.setCodeValueWithRefs(data.addressValidationCode6, value.charAt(5));
            }
        },
        reset: function () {
            data.errorMessage.value = '';
            data.addressCodeHasBeenSent.value = false;
            data.isValidated.value = false;
            data.addressValidationCode1.value = '';
            data.addressValidationCode2.value = '';
            data.addressValidationCode3.value = '';
            data.addressValidationCode4.value = '';
            data.addressValidationCode5.value = '';
            data.addressValidationCode6.value = '';
        }
    };
}

export default defineComponent({
    props: {
        modelValue: String,
        disableValidation: Boolean,
        disableEmail: Boolean,
        disableSms: Boolean,
        enableUsername: Boolean,
        placeholder: String,
        reset: Number,
        validationFailed: Boolean,
        withPassword: Boolean,
        allowSkip: Boolean,
        disablePassword: Boolean,
        disableFocus: Boolean
    },
    emits: ['update:modelValue', 'validated', 'choosePassword'],
    setup: function (props, ctx: any) {
        const data = getData(props);
        const computed = getComputed(data, props);
        const methods = getMethods(data, computed, ctx, props);

        onMounted(() => {
            if (data.username.value && !props.disableFocus) {
                data.username.value.focus();
            }
        });

        watch(() => props.modelValue as string, (currentVal: string, oldVal: string) => {
            if (props.disableValidation) {
                ctx.emit('validated', currentVal);
            } else {
                methods.reset();
            }
        });

        watch(() => props.reset as number, (currentVal: number, oldVal: number) => {
            methods.reset();
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});

